import React, { isValidElement, useState } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane, Container } from "reactstrap";
import classnames from "classnames";

import { Apify } from '../Apify';
import { R } from '../routes';
import { DelistButton, TradeButton } from "../c/Trade";
import AddToPortfolio from "../c/AddToPortfolio";
import CollectionChecker from "../c/CollectionChecker";
import { RangedChart, LargeChartInner } from "../c/Chart";
import LogoAndInfo from "../c/LogoAndInfo";
import { FinancialData as FinancialDataComponent } from "../c/FinancialData";
import { CompanyInfo as CompanyInfoComponent } from "../c/CompanyInfo";
import { KeyFigures as KeyFiguresComponent } from "../c/KeyFigures";
import { T, TraksConsumer } from '../traks';
import { validatePremium } from "../misc";
import Me from "../Me"
import NewsTeasers from '../c/NewsTeasers.js';
import { SponsorBanner } from '../c/SponsorBanner.js';

function LargeStockChart(props) {
  const info = <LogoAndInfo isPremium={props.isUserPremium} exchange={props.exchange} ticker={props.ticker} name={props.name} beta={props.beta} stockType={props.stockType} showNotification={true} />;
  return (
    <RangedChart
      range_in="query"
      type="stock"
      info={info}
      inner={LargeChartInner}
      {...props}
    />
  );
}

function FinancialData(props) {
  //TODO: Get proper Financial data Info
  const info = <LogoAndInfo isPremium={props.isUserPremium} exchange={props.exchange} ticker={props.ticker} name={props.name} beta={props.beta} stockType={props.stockType} />;
  return <FinancialDataComponent info={info} incomeStatement = {props.incomeStatement} {...props} />;
}

function CompanyInfo(props) {
  //TODO: Get proper Company Info
  const info = <LogoAndInfo isPremium={props.isUserPremium} exchange={props.exchange} ticker={props.ticker} name={props.name} beta={props.beta} stockType={props.stockType} />;
  return <CompanyInfoComponent info={info} {...props} />;
}

function KeyFigures(props) {
  const info = <LogoAndInfo isPremium={props.isUserPremium} exchange={props.exchange} ticker={props.ticker} name={props.name} beta={props.beta} stockType={props.stockType} />;
  return <KeyFiguresComponent info={info} {...props} />
}

const Ticker = (props) => {
  const me = Me.get() ? Me.get().User : null;
  const isUserPremium = me && me.Subscription && validatePremium(me) ? true : false;
  const isUserGod = me && me.IsGod && me.IsGod === true ? true : false;
  const [activeTab, setActiveTab] = useState("chart");

  const tabs = [
    {
      label: <T>Chart</T>,
      key: "chart",
      content: (
        <LargeStockChart exchange={props.exchange} ticker={props.ticker} isUserPremium={isUserPremium} name={props.name} beta={props.beta} stockType={props.stockType} />
      ),
      isPremiumFeature: false,
    },

    {
      label: <T>Company Information</T>,
      key: "company",
      content: <CompanyInfo exchange={props.exchange} ticker={props.ticker} isUserPremium={isUserPremium} name={props.name} beta={props.beta} stockType={props.stockType} businessDescription={props.businessDescription} companyGeneralInfo={props.companyGeneralInfo} marketData={props.marketData} />,
      isPremiumFeature: false,
    },

    {
      label: <T>Financial Information</T>,
      key: "financial",
      content: (
        <FinancialData exchange={props.exchange} ticker={props.ticker} isUserPremium={isUserPremium} name={props.name} beta={props.beta} stockType={props.stockType} incomeStatement={props.incomeStatement} balanceSheet={props.balanceSheet} cashFlow={props.cashFlow}/>
      ),
      isPremiumFeature: false,
    },

    {
      label: <T>Key Figures</T>,
      key: 'key_figures',
      content: (
        <KeyFigures exchange={props.exchange} ticker={props.ticker} isUserPremium={isUserPremium} name={props.name} beta={props.beta} stockType={props.stockType} efficiencyRatios={props.efficiencyRatios} valuationRatios={props.valuationRatios} />
      ),
      isPremiumFeature: false,
    },
  ];

  return (
    <Container className="px-0 pb-4 pt-3">
      <Nav className="ad_graph_tabs" tabs>
        {tabs.map((tab) => (
          <NavItem key={tab.key}>
            <NavLink
              id={`nav-link-${tab.key}`}
              className={classnames("ad_tab_custom_nav_link", {
                active: activeTab === tab.key,
                "disabled-tab": tab.restricted,
              })}
              onClick={() => !tab.restricted && setActiveTab(tab.key)}
            >
              {tab.label}
              {tab.isPremiumFeature && (
                <span className="ad_premium_badge_in_tab">Premium</span>
              )}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={activeTab}>
        {tabs.map((tab) => (
          <TabPane style={{
            filter: tab.isPremiumFeature === true && isUserPremium === false ? 'blur(4px)' : 'none'
          }} tabId={tab.key} key={tab.key}>
            {tab.content}
          </TabPane>
        ))}
      </TabContent>


      <div className="gap-20 px-3 d-flex align-items-center">
        <TradeButton exchange={props.exchange} ticker={props.ticker} isUserPremium={isUserPremium} type={props.stockType} isHalted={props.isHalted} />
        <CollectionChecker exchange={props.exchange} ticker={props.ticker} />
        <AddToPortfolio exchange={props.exchange} ticker={props.ticker} />
        {isUserGod && <DelistButton userId={me.Id} exchange={props.exchange} ticker={props.ticker} />}
        <div className="pb-5">&nbsp;</div>
      </div>
      <SponsorBanner placement={1} />
      <NewsTeasers />
      <SponsorBanner placement={5} />
    </Container>
  );
}

const TickerApi = Apify("/z/stock_info")
    .with_query_from_props(props => ({exchange: props.exchange, ticker: props.ticker, lang: R.get_current_language()}))
    .with_props_from_response(response => {
        return {
            name: response.Name,
            beta: response.BetaValue,
            stockType: response.StockType,
            incomeStatement: response.IncomeStatement,
            numberOfShareHolders: response.NumberOfShareHolders,
            marketData: response.MarketData,
            efficiencyRatios: response.EfficiencyRatios,
            companyGeneralInfo: response.CompanyGeneralInfo,
            businessDescription: response.BusinessDescription,
            valuationRatios: response.ValuationRatios,
            balanceSheet: response.BalanceSheet,
            cashFlow: response.CashFlow,
            isHalted: response.HaltTs !== 0 
        };
    })
    .wrap((props) => (
        <TraksConsumer>
            <Ticker {...props} />
        </TraksConsumer>
    ))

export default TickerApi;


// WEBPACK FOOTER //
// ./src/p/Ticker.js